import { createContext, type ReactNode, use, useEffect, useMemo, useRef } from "react";

import { getRequestContext } from "~/api/requestContextStore";
import { apiConfig } from "~/config/api";
import { useAuth } from "~/features/auth";
import { getAccessToken } from "~/features/auth/services/keycloakService";

import { CreateCollector, type WatchCreate } from "./createWatch";
import { notificationWebSocketUrl } from "./notificationSocketUrl";
import { readCreateCompleted } from "./readCreateCompleted";
import { authenticateMessage } from "./socketAuthenticate";
import { isRecord, summariseFrame } from "./socketFrame";

export interface NotificationSocket {
  watchCreate: WatchCreate;
  /** Resolves once the worker has accepted this session's credentials. */
  whenAuthenticated: (timeoutMs: number) => Promise<void>;
  /** Sends one JSON frame when the socket is open. */
  send: (message: Record<string, unknown>) => void;
  /** Hears every decoded frame, including presence. */
  subscribe: (handler: (message: unknown) => void) => () => void;
  /** Fires with the current state, then again after each connect and drop. */
  subscribeConnection: (handler: (ready: boolean) => void) => () => void;
}

/** Renews presence membership. The worker expires a quiet member after 45s. */
const PING_INTERVAL_MS = 15_000;

const NotificationSocketContext = createContext<NotificationSocket | null>(null);

export const useNotificationSocket = (): NotificationSocket | null =>
  use(NotificationSocketContext);

const RECONNECT_CAP_MS = 15_000;

/**
 * App-shell socket for create completion.
 *
 * The session has to exist before the click. The worker drops a frame when
 * nobody is connected, and a late socket cannot replay it.
 */
export const NotificationSocketProvider = ({ children }: { children: ReactNode }) => {
  const { tenantId } = useAuth();
  const collectors = useRef(new Set<CreateCollector>());
  const authenticated = useRef(false);
  const authListeners = useRef(new Set<() => void>());
  const messageListeners = useRef(new Set<(message: unknown) => void>());
  const connectionListeners = useRef(new Set<(ready: boolean) => void>());
  const socketRef = useRef<WebSocket | null>(null);

  const socketApi = useMemo<NotificationSocket>(
    () => ({
      watchCreate: (options) => {
        const collector: CreateCollector = new CreateCollector(options, () =>
          collectors.current.delete(collector),
        );

        collectors.current.add(collector);

        return {
          result: collector.result,
          arm: () => collector.arm(),
          cancel: () => collector.cancel(),
        };
      },
      whenAuthenticated: (timeoutMs) => {
        if (authenticated.current) {
          return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
          const ready = () => {
            clearTimeout(timer);
            authListeners.current.delete(ready);
            resolve();
          };
          const timer = setTimeout(() => {
            authListeners.current.delete(ready);
            reject(new Error("The notification socket did not authenticate."));
          }, timeoutMs);

          authListeners.current.add(ready);
        });
      },
      send: (message) => {
        const current = socketRef.current;

        if (current?.readyState === WebSocket.OPEN) {
          current.send(JSON.stringify(message));
        }
      },
      subscribe: (handler) => {
        messageListeners.current.add(handler);

        return () => {
          messageListeners.current.delete(handler);
        };
      },
      subscribeConnection: (handler) => {
        connectionListeners.current.add(handler);
        handler(authenticated.current);

        return () => {
          connectionListeners.current.delete(handler);
        };
      },
    }),
    [],
  );

  useEffect(() => {
    if (!tenantId || typeof WebSocket === "undefined") {
      console.warn("[create-completion] socket not started", {
        tenantId: tenantId ?? null,
        hasWebSocket: typeof WebSocket !== "undefined",
      });
      return;
    }

    let socket: WebSocket | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let pingTimer: ReturnType<typeof setInterval> | undefined;
    let closed = false;
    let attempt = 0;
    const active = collectors.current;
    const listeners = authListeners.current;

    const markAuthenticated = () => {
      authenticated.current = true;

      for (const listener of [...listeners]) {
        listener();
      }

      for (const listener of [...connectionListeners.current]) {
        listener(true);
      }
    };

    const deliver = (message: unknown) => {
      const frame = readCreateCompleted(message);

      if (!frame) {
        return;
      }

      for (const collector of [...active]) {
        collector.add(frame);
      }
    };

    const connect = () => {
      if (closed) {
        return;
      }

      const url = notificationWebSocketUrl(
        window.location.href,
        apiConfig.notificationServiceBaseUrl,
      );
      console.warn("[create-completion] socket connecting", {
        url,
        base: apiConfig.notificationServiceBaseUrl || "(page origin)",
      });
      const next = new WebSocket(url);
      socket = next;
      socketRef.current = next;

      if (pingTimer) {
        clearInterval(pingTimer);
      }

      pingTimer = setInterval(() => {
        if (next.readyState === WebSocket.OPEN) {
          next.send(JSON.stringify({ action: "ping" }));
        }
      }, PING_INTERVAL_MS);

      const authenticate = () => {
        void getAccessToken().then((accessToken) => {
          if (next.readyState !== WebSocket.OPEN) {
            return;
          }

          const context = getRequestContext();

          next.send(
            JSON.stringify(
              authenticateMessage(
                context?.tenantId,
                context?.userProfileId,
                apiConfig.apiKey,
                accessToken,
                context?.securityContext,
              ),
            ),
          );
          console.warn("[create-completion] socket authenticate sent", {
            tenantId: context?.tenantId ?? null,
            hasUser: Boolean(context?.userProfileId),
            hasApiKey: Boolean(apiConfig.apiKey),
            hasToken: Boolean(accessToken),
            hasSecurityContext: Boolean(context?.securityContext || context?.tenantId),
          });
        });
      };

      next.addEventListener("open", () => {
        attempt = 0;
        console.warn("[create-completion] socket open", { url });
      });

      next.addEventListener("message", (event) => {
        if (typeof event.data !== "string") {
          console.warn("[create-completion] socket non-text frame");
          return;
        }

        try {
          const parsed = JSON.parse(event.data) as unknown;
          console.warn("[create-completion] socket message", summariseFrame(parsed));

          for (const listener of [...messageListeners.current]) {
            listener(parsed);
          }

          if (isRecord(parsed) && parsed.action === "connected") {
            if (parsed.requiresAuth === false) {
              markAuthenticated();
            } else {
              authenticate();
            }
          }

          if (isRecord(parsed) && parsed.action === "authenticated") {
            markAuthenticated();
          }

          deliver(parsed);
        } catch {
          console.warn("[create-completion] socket non-json frame");
        }
      });

      next.addEventListener("error", () => {
        console.warn("[create-completion] socket error", { url, readyState: next.readyState });
      });

      next.addEventListener("close", (event) => {
        console.warn("[create-completion] socket closed", {
          url,
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        });
        if (socket === next) {
          authenticated.current = false;
          socketRef.current = null;

          if (pingTimer) {
            clearInterval(pingTimer);
            pingTimer = undefined;
          }

          for (const listener of [...connectionListeners.current]) {
            listener(false);
          }
        }

        if (closed || socket !== next) {
          return;
        }

        if (event.code === 4001) {
          attempt = 0;
        }

        const delay = Math.min(RECONNECT_CAP_MS, 1000 * 2 ** attempt);
        attempt += 1;
        reconnectTimer = setTimeout(connect, delay);
      });
    };

    connect();

    return () => {
      closed = true;
      authenticated.current = false;
      socketRef.current = null;

      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }

      clearInterval(pingTimer);
      socket?.close();

      for (const collector of [...active]) {
        collector.cancel();
      }
    };
  }, [tenantId]);

  return <NotificationSocketContext value={socketApi}>{children}</NotificationSocketContext>;
};

/** Deterministic socket for unit tests (no WebSocket). */
export const NotificationSocketTestProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: NotificationSocket;
}) => <NotificationSocketContext value={value}>{children}</NotificationSocketContext>;
